import { contents, banners } from '@/mocks/contents';

export interface ContentItem {
  id: string;
  title: string;
  category: string;
  cover: string;
  genres: string[];
  region: string;
  year: number;
  subtitle: string;
  quality: string;
  duration: string;
  episodes: number;
  rating: number;
  hot: number;
  updatedAt: string;
  description: string;
}

export interface BannerItem {
  id: string;
  contentId: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
}

export type CategoryKey = 'movie' | 'tv' | 'variety';

export const CATEGORY_META: Record<string, { name: string; h1: string; desc: string }> = {
  movie: {
    name: '电影',
    h1: '电影频道',
    desc: '海量中文字幕电影，动作、科幻、爱情、悬疑、古装等多种类型一网打尽。',
  },
  tv: {
    name: '电视剧',
    h1: '电视剧频道',
    desc: '精选热门电视剧，都市、古装、悬疑、医疗、年代剧持续更新，中文字幕同步。',
  },
  variety: {
    name: '综艺',
    h1: '综艺频道',
    desc: '轻松好玩的综艺节目，脱口秀、真人秀、美食、音乐、时尚，欢乐不停歇。',
  },
};

export const allContents = contents as ContentItem[];
export const allBanners = banners as BannerItem[];

export function getCategoryName(category: string): string {
  return CATEGORY_META[category]?.name || '影视';
}

export function getContentById(id: string): ContentItem | undefined {
  return allContents.find((item) => item.id === String(id));
}

export function getByCategory(type: string): ContentItem[] {
  return allContents.filter((item) => item.category === type);
}

export function searchContents(keyword: string): ContentItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allContents.filter((item) => {
    const haystack = [item.title, ...item.genres, item.region, item.subtitle, getCategoryName(item.category)]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function sortContents(list: ContentItem[], sort: 'updated' | 'hot'): ContentItem[] {
  const copy = [...list];
  if (sort === 'hot') {
    return copy.sort((a, b) => b.hot - a.hot);
  }
  return copy.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function getLatest(limit = 8): ContentItem[] {
  return sortContents(allContents, 'updated').slice(0, limit);
}

export function getHot(limit = 8): ContentItem[] {
  return sortContents(allContents, 'hot').slice(0, limit);
}

export function getRelated(item: ContentItem, limit = 6): ContentItem[] {
  return allContents
    .filter((c) => c.id !== item.id)
    .map((c) => {
      let score = 0;
      if (c.category === item.category) score += 3;
      if (c.region === item.region) score += 2;
      score += c.genres.filter((g) => item.genres.includes(g)).length * 2;
      return { item: c, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function collectFacets(list: ContentItem[]) {
  const genres = Array.from(new Set(list.flatMap((item) => item.genres)));
  const regions = Array.from(new Set(list.map((item) => item.region)));
  const years = Array.from(new Set(list.map((item) => item.year))).sort((a, b) => b - a);
  return { genres, regions, years };
}

export function formatHot(hot: number): string {
  if (hot >= 10000) {
    return `${(hot / 10000).toFixed(1)} 万`;
  }
  return String(hot);
}

export const PAGE_SIZE = 8;